import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createParts } from '../../api/partsAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';

const PartsImporter = ({ onComplete }) => {
  const handleDataImport = async (rows, onProgress) => {
    let count = 0;
    for (const row of rows) {
      try {
        await createParts({
          oemNumber: row.oemNumber,
          partNumber: row.partNumber,
          sourceOfSupplyId: row.sourceOfSupplyId,
          categoryId: row.categoryId,
          createdAt: row.createdAt,
          updatedAt: row.updatedAt,
          createdBy: row.createdBy,
          updatedBy: row.updatedBy,
          description: row.description,
          supplierId: row.supplierId,
          weight: row.weight,
          lastPrice: row.lastPrice,
          pricingDate: row.pricingDate,
        });
      } catch (err) {
        console.error('Failed to import part', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>parts</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className='mb-4'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => handleDownloadTemplateHelper('parts')}
          >
            Download Template
          </button>
        </div>
        <div className='mb-4'>
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>oemNumber</td>
                      <td>Text</td>
                      <td>Original equipment manufacturer number</td>
                    </tr>
                    <tr>
                      <td>partNumber</td>
                      <td>Text</td>
                      <td>Internal part number or code</td>
                    </tr>
                    <tr>
                      <td>sourceOfSupplyId</td>
                      <td>Lookup</td>
                      <td>Valid manufacturer ID from the system</td>
                    </tr>
                    <tr>
                      <td>categoryId</td>
                      <td>Lookup</td>
                      <td>Valid parts category ID from the system</td>
                    </tr>
                    <tr>
                      <td>description</td>
                      <td>Text</td>
                      <td>Description of the part</td>
                    </tr>
                    <tr>
                      <td>supplierId</td>
                      <td>Lookup</td>
                      <td>Preferred supplier ID from the system</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey='1'>
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size='sm'>
                  <thead className='table-light'>
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {/*<td>materialNumber</td>*/}
                      <td>Text</td>
                      <td>Material number (optional)</td>
                    </tr>
                    <tr>
                      <td>weight</td>
                      <td>Number</td>
                      <td>Part weight</td>
                    </tr>
                    <tr>
                      <td>lastPrice</td>
                      <td>Number</td>
                      <td>Last purchase price</td>
                    </tr>
                    <tr>
                      <td>pricingDate</td>
                      <td>Date</td>
                      <td>Date when the last price was recorded - DD/MM/YYYY format</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity='parts'
          requiredFields={[
            { key: 'oemNumber', type: 'string', required: true },
            { key: 'partNumber', type: 'string', required: true },
            {
              key: 'sourceOfSupplyId',
              type: 'lookup',
              required: true,
              api: 'manufacturers',
              nameField: 'manufacturer',
            },
            {
              key: 'categoryId',
              type: 'lookup',
              required: true,
              api: 'parts-category',
              nameField: 'name',
            },
            { key: 'description', type: 'string', required: true },
            {
              key: 'supplierId',
              type: 'lookup',
              required: true,
              api: 'suppliers',
              nameField: 'name',
            },
            { 
              key: 'pricingDate',
              type: 'date',
              required: false,
            }
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default PartsImporter;
